import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  getHostels, getHostelAdmissions, getHostelPayments, deleteHostelPayment,
  getHostelAdmissionOptions, getAttendanceHistory, getMealsHistory, getInvoices, getInvoice,
  getBillingAdjustments, addBillingAdjustment, deleteBillingAdjustment, getResidentHistoryByCnic,
} from '../api';
import { FiDollarSign, FiUser } from 'react-icons/fi';
import { usePermissions } from '../utils/permissions';
import ResidentProfileModal from './ResidentProfileModal';

export default function HostelFinances() {
  const { can } = usePermissions();
  const canDelete = can('hostel_finances', 'delete');
  const [hostels, setHostels] = useState([]);
  const [admissions, setAdmissions] = useState([]);
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [hostFilter, setHostFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [activeTab, setActiveTab] = useState('overview');
  const [campuses, setCampuses] = useState([]);
  const [courses, setCourses] = useState([]);
  const [profileRecord, setProfileRecord] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (hostFilter) params.hostel_id = hostFilter;
      if (statusFilter) params.status = statusFilter;
      const [hRes, aRes, pRes] = await Promise.all([
        getHostels(),
        getHostelAdmissions(params),
        getHostelPayments(hostFilter ? { hostel_id: hostFilter } : {})
      ]);
      setHostels(hRes.data);
      setAdmissions(aRes.data);
      setPayments(pRes.data);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [hostFilter, statusFilter]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    getHostelAdmissionOptions().then(res => {
      setCampuses(res.data.campuses || []);
      setCourses(res.data.courses || []);
    }).catch(() => {});
  }, []);

  const admissionsById = useMemo(() => Object.fromEntries(admissions.map(a => [a.id, a])), [admissions]);
  const viewProfile = (admissionId, fallbackName) => {
    setProfileRecord(admissionsById[admissionId] || { id: admissionId, full_name: fallbackName });
  };

  const totalDues = admissions.reduce((s, a) => s + (Number(a.total_dues) || 0), 0);
  const totalPaid = admissions.reduce((s, a) => s + (Number(a.total_paid) || 0), 0);
  const totalBalance = totalDues - totalPaid;
  const activeCount = admissions.filter(a => a.status === 'active').length;

  const handleDeletePayment = async (id) => {
    if (!window.confirm('Delete this payment record?')) return;
    try {
      await deleteHostelPayment(id);
      load();
    } catch (e) { alert('Failed to delete payment'); }
  };

  const tabStyle = (tab) => ({
    padding: '8px 20px', border: '1px solid #e5e7eb', borderRadius: '6px', cursor: 'pointer',
    background: activeTab === tab ? '#0e1e45' : 'white',
    color: activeTab === tab ? '#fff' : '#374151',
    fontWeight: '600', fontSize: '14px', transition: 'all 0.2s'
  });

  return (
    <div className="card">
      <div className="card-header">
        <div>
          <h2 className="card-title"><FiDollarSign /> Hostel Finances</h2>
          <p style={{ color: '#6b7280', margin: '4px 0 0' }}>Financial overview and payment records</p>
        </div>
      </div>

      {/* Summary Cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '14px', margin: '20px 0' }}>
        {[
          { label: 'Active Residents', value: activeCount, color: '#3b82f6' },
          { label: 'Total Dues', value: `Rs. ${totalDues.toLocaleString()}`, color: '#f59e0b' },
          { label: 'Total Paid to Hostel', value: `Rs. ${totalPaid.toLocaleString()}`, color: '#0e1e45' },
          { label: 'Total Pending', value: `Rs. ${totalBalance.toLocaleString()}`, color: totalBalance > 0 ? '#ef4444' : '#0e1e45' },
          { label: 'Total Payments', value: payments.length, color: '#8b5cf6' },
        ].map(c => (
          <div key={c.label} style={{ background: 'white', border: `1px solid ${c.color}44`, borderRadius: '10px', padding: '16px', textAlign: 'center', boxShadow: '0 1px 4px rgba(0,0,0,0.06)' }}>
            <div style={{ fontSize: '20px', fontWeight: '700', color: c.color }}>{c.value}</div>
            <div style={{ fontSize: '11px', color: '#6b7280', marginTop: '4px' }}>{c.label}</div>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', marginBottom: '16px' }}>
        <select className="form-control" style={{ minWidth: '160px' }} value={hostFilter} onChange={e => setHostFilter(e.target.value)}>
          <option value="">All Hostels</option>
          {hostels.map(h => <option key={h.id} value={h.id}>{h.name}</option>)}
        </select>
        <select className="form-control" style={{ minWidth: '130px' }} value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
          <option value="">All Status</option>
          <option value="active">Active</option>
          <option value="exited">Exited</option>
        </select>
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: '8px', marginBottom: '16px', flexWrap: 'wrap' }}>
        <button style={tabStyle('overview')} onClick={() => setActiveTab('overview')}>Per Resident</button>
        <button style={tabStyle('payments')} onClick={() => setActiveTab('payments')}>Payment History</button>
        <button style={tabStyle('hostel')} onClick={() => setActiveTab('hostel')}>By Hostel</button>
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', padding: '40px' }}><div className="spinner"></div></div>
      ) : activeTab === 'overview' ? (
        <div className="table-container">
          <table className="data-table">
            <thead>
              <tr>
                <th>#</th>
                <th>Resident</th>
                <th>Hostel</th>
                <th>Admission Date</th>
                <th>Exit Date</th>
                <th>Months</th>
                <th>Rent/mo</th>
                <th>Total Dues</th>
                <th>Paid</th>
                <th>Balance</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {admissions.map((a, i) => (
                <tr key={a.id}>
                  <td>{i + 1}</td>
                  <td>
                    <strong>{a.full_name}</strong><br /><small style={{ color: '#9ca3af' }}>{a.cnic || ''}</small>{' '}
                    <button className="btn btn-small btn-secondary" title="View resident profile" onClick={() => viewProfile(a.id, a.full_name)}><FiUser size={12} /></button>
                  </td>
                  <td style={{ color: '#0e1e45' }}>{a.hostel_name}</td>
                  <td>{a.date_of_admission ? new Date(a.date_of_admission).toLocaleDateString('en-PK') : '—'}</td>
                  <td>{a.date_of_exit ? new Date(a.date_of_exit).toLocaleDateString('en-PK') : '—'}</td>
                  <td style={{ textAlign: 'center' }}>{a.months_elapsed || 0}</td>
                  <td>Rs. {Number(a.monthly_rent || 0).toLocaleString()}</td>
                  <td style={{ color: '#f59e0b' }}>Rs. {Number(a.total_dues || 0).toLocaleString()}</td>
                  <td style={{ color: '#0e1e45' }}>Rs. {Number(a.total_paid || 0).toLocaleString()}</td>
                  <td style={{ color: (a.balance || 0) > 0 ? '#ef4444' : '#0e1e45', fontWeight: '600' }}>
                    Rs. {Number(a.balance || 0).toLocaleString()}
                  </td>
                  <td>
                    <span style={{ padding: '3px 8px', borderRadius: '10px', fontSize: '11px', background: a.status === 'active' ? 'rgba(4,120,87,0.15)' : 'rgba(107,114,128,0.15)', color: a.status === 'active' ? '#0e1e45' : '#6b7280' }}>{a.status}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : activeTab === 'payments' ? (
        <div className="table-container">
          <table className="data-table">
            <thead>
              <tr>
                <th>#</th>
                <th>Resident</th>
                <th>Hostel</th>
                <th>Amount</th>
                <th>Payment Date</th>
                <th>Months Covered</th>
                <th>Notes</th>
                {canDelete && <th>Actions</th>}
              </tr>
            </thead>
            <tbody>
              {payments.length === 0 ? (
                <tr><td colSpan={canDelete ? 8 : 7} style={{ textAlign: 'center', padding: '30px', color: '#9ca3af' }}>No payments recorded yet.</td></tr>
              ) : payments.map((p, i) => (
                <tr key={p.id}>
                  <td>{i + 1}</td>
                  <td>
                    {p.full_name}{' '}
                    <button className="btn btn-small btn-secondary" title="View resident profile" onClick={() => viewProfile(p.admission_id, p.full_name)}><FiUser size={12} /></button>
                  </td>
                  <td style={{ color: '#0e1e45' }}>{p.hostel_name}</td>
                  <td style={{ color: '#0e1e45', fontWeight: '600' }}>Rs. {Number(p.amount).toLocaleString()}</td>
                  <td>{p.payment_date ? new Date(p.payment_date).toLocaleDateString('en-PK') : '—'}</td>
                  <td style={{ textAlign: 'center' }}>{p.months_covered || '—'}</td>
                  <td>{p.notes || '—'}</td>
                  {canDelete && (
                    <td>
                      <button className="btn btn-danger btn-small" style={{ fontSize: '11px' }} onClick={() => handleDeletePayment(p.id)}>Delete</button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        /* By Hostel summary */
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '16px' }}>
          {hostels.filter(h => !hostFilter || h.id === Number(hostFilter)).map(hostel => {
            const hAdm = admissions.filter(a => a.hostel_id === hostel.id);
            const hDues = hAdm.reduce((s, a) => s + (Number(a.total_dues) || 0), 0);
            const hPaid = hAdm.reduce((s, a) => s + (Number(a.total_paid) || 0), 0);
            const hBal = hDues - hPaid;
            return (
              <div key={hostel.id} style={{ background: 'white', border: '1px solid #e5e7eb', borderRadius: '12px', padding: '20px', boxShadow: '0 1px 4px rgba(0,0,0,0.06)' }}>
                <h4 style={{ color: '#0e1e45', margin: '0 0 12px' }}>{hostel.name}</h4>
                <div style={{ display: 'grid', gap: '8px' }}>
                  {[
                    { label: 'Residents', value: hAdm.length },
                    { label: 'Active', value: hAdm.filter(a => a.status === 'active').length },
                    { label: 'Total Dues', value: `Rs. ${hDues.toLocaleString()}`, color: '#f59e0b' },
                    { label: 'Total Paid', value: `Rs. ${hPaid.toLocaleString()}`, color: '#0e1e45' },
                    { label: 'Balance', value: `Rs. ${hBal.toLocaleString()}`, color: hBal > 0 ? '#ef4444' : '#0e1e45' },
                  ].map(r => (
                    <div key={r.label} style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <span style={{ color: '#6b7280', fontSize: '13px' }}>{r.label}</span>
                      <span style={{ color: r.color || '#e2e8f0', fontWeight: '600', fontSize: '13px' }}>{r.value}</span>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {profileRecord && (
        <ResidentProfileModal
          admission={profileRecord}
          campuses={campuses}
          courses={courses}
          onClose={() => setProfileRecord(null)}
          fetchAttendance={(id) => getAttendanceHistory({ admission_id: id })}
          fetchMeals={(id) => getMealsHistory({ admission_id: id })}
          fetchPayments={(id) => getHostelPayments({ admission_id: id })}
          fetchInvoices={(id) => getInvoices({ admission_id: id })}
          fetchInvoiceDetail={(id) => getInvoice(id)}
          fetchAdjustments={(id) => getBillingAdjustments({ admission_id: id })}
          fetchResidentHistory={(cnic) => getResidentHistoryByCnic(cnic)}
          canManageAdjustments
          onAddAdjustment={addBillingAdjustment}
          onDeleteAdjustment={deleteBillingAdjustment}
        />
      )}
    </div>
  );
}
